import { action, makeObservable, observable } from 'mobx';

import { hasValue } from 'src/packages/utils/has-value';

import type { TRule } from './mappers';
import type { IDirectoriesStorage } from '../directories-storage-service/directories-storage-service';
import type { DirectoryRecord } from '../directory-service/entities/directory-record.entity';
import type { TControlView } from '../directory-service/types/controls-views.type';
import type { ValidationService } from '../directory-service/validation-service/validation-service';

import { mapControlRules } from './mappers';
import { handleCalculatedValueRule } from './rule-handlers/handle-calculated-value-rule';
import { handleConcatinationRule } from './rule-handlers/handle-concatination-rule';
import { handleEnableIfRule } from './rule-handlers/handle-enable-if-rule';
import { handleFilterOptionsRule } from './rule-handlers/handle-filter-options-rule';
import { handleRequiredIfRule } from './rule-handlers/handle-required-if-rule';
import { handleRestrictionsRule } from './rule-handlers/handle-restrictions-rule';
import { handleValueSourceRule } from './rule-handlers/handle-value-source-rule';

export class ControlRulesService {
  private readonly directoriesStorageService: IDirectoriesStorage;
  private readonly validationService: ValidationService;
  readonly rules = observable.map<string, TRule[]>();

  constructor(directoriesStorageService: IDirectoriesStorage, validationService: ValidationService) {
    this.directoriesStorageService = directoriesStorageService;
    this.validationService = validationService;

    makeObservable(this);
  }

  @action.bound
  mapRulesFromControlViews(controls: TControlView[]): void {
    for (const control of controls) {
      const rules = mapControlRules(control);

      for (const { managmentControls, rule } of rules) {
        for (const managmentControl of managmentControls) {
          const rules: TRule[] = [...(this.rules.get(managmentControl) ?? []), rule];

          this.rules.set(managmentControl, rules);
        }

        if (rule.type === 'filterOptions') {
          const rules: TRule[] = [...(this.rules.get(rule.dependentField) ?? []), rule];

          this.rules.set(rule.dependentField, rules);
        }
      }
    }
  }

  @action.bound
  onControlValueChange(
    record: DirectoryRecord,
    controlViewMap: Map<string, TControlView>,
    fieldId: string,
    value: unknown,
    onControlValueChange: (record: DirectoryRecord, fieldId: string, value: unknown) => void,
  ): void {
    const rules = this.rules.get(fieldId);
    const recordData = { ...record.data, [fieldId]: value };

    if (!rules || !rules.length) {
      return;
    }

    const fieldsRulesData = { ...record.fieldRulesData };

    rules.forEach((rule) => {
      switch (rule.type) {
        case 'valueSource': {
          if (!rule.dependentField) {
            console.error('rule.dependentField is not presented');
            break;
          }

          const { value } = handleValueSourceRule(this.directoriesStorageService, recordData, rule.ruleView.sources);

          onControlValueChange(record, rule.dependentField, value);

          break;
        }

        case 'restrictions': {
          const controlView = controlViewMap.get(fieldId);

          if (!controlView) {
            console.error('controlView is not presented');
            break;
          }

          const data = handleRestrictionsRule(recordData, controlView, rule, value);

          if (data) {
            onControlValueChange(record, fieldId, data.value);
          }

          break;
        }

        case 'enableIf': {
          if (!rule.dependentField) {
            console.error(`rule.dependentField is not presented. fieldId: ${fieldId}`);
            break;
          }

          const { isDisabled } = handleEnableIfRule(recordData, rule);

          fieldsRulesData[rule.dependentField] = { ...(fieldsRulesData[rule.dependentField] ?? {}), isDisabled };

          if (isDisabled) {
            onControlValueChange(record, rule.dependentField, null);
          }

          break;
        }

        case 'filterOptions': {
          const depControlView = controlViewMap.get(rule.dependentField);

          if (!depControlView) {
            console.error(`depControlView is not presented. fieldId: ${fieldId}`);
            break;
          }

          if (depControlView.control !== 'ComboBox' && depControlView.control !== 'MultiComboBox') {
            break;
          }

          const { allowedOptions } = handleFilterOptionsRule(
            this.directoriesStorageService,
            recordData,
            depControlView,
            rule,
          );

          fieldsRulesData[rule.dependentField] = { ...(fieldsRulesData[rule.dependentField] ?? {}), allowedOptions };
          const depControlValue = recordData[depControlView.fieldId];

          if (depControlView.control === 'ComboBox') {
            if (
              hasValue(depControlValue) &&
              typeof depControlValue !== 'number' &&
              typeof depControlValue !== 'string'
            ) {
              console.error('wrong depControlValue type');
              break;
            }

            if (!!allowedOptions && hasValue(depControlValue) && !allowedOptions[depControlValue]) {
              onControlValueChange(record, depControlView.fieldId, null);
            }
          }

          if (depControlView.control === 'MultiComboBox') {
            if (hasValue(depControlValue) && !Array.isArray(depControlValue)) {
              console.error('wrong depControlValue type');
              break;
            }

            if (!!allowedOptions && !!depControlValue?.length) {
              const filteredValue = depControlValue.filter((v) => allowedOptions[v]);

              if (filteredValue.length !== depControlValue.length) {
                onControlValueChange(record, depControlView.fieldId, filteredValue);
              }
            }
          }

          break;
        }

        case 'concatination': {
          const depControlView = controlViewMap.get(rule.dependentField);

          if (!depControlView || depControlView.control !== 'Label') {
            console.error(`cannot find corresponding control view. fieldId: ${fieldId}`);
            break;
          }

          const { value } = handleConcatinationRule(this.directoriesStorageService, recordData, rule);

          if (depControlView.concatination?.concatinationType === 'display') {
            fieldsRulesData[rule.dependentField] = {
              ...(fieldsRulesData[rule.dependentField] ?? {}),
              displayValue: value,
            };
          } else {
            onControlValueChange(record, depControlView.fieldId, value);
          }

          break;
        }

        case 'calculatedValue': {
          const depControlView = controlViewMap.get(rule.dependentField);

          if (!depControlView) {
            console.error(`cannot find corresponding control view. fieldId: ${fieldId}`);
            break;
          }

          const { value } = handleCalculatedValueRule(this.directoriesStorageService, recordData, rule.ruleView);

          if (depControlView.control === 'CheckBox') {
            if (!!value !== recordData[rule.dependentField]) {
              onControlValueChange(record, rule.dependentField, value);
            }

            break;
          }

          onControlValueChange(record, rule.dependentField, value);

          break;
        }

        case 'requiredIf': {
          const depControlView = controlViewMap.get(rule.dependentField);

          if (!depControlView) {
            console.error(`cannot find corresponding control view. fieldId: ${fieldId}`);
            break;
          }

          const { isRequired } = handleRequiredIfRule(this.directoriesStorageService, recordData, rule);
          this.validationService.setIsControlRequired(`${record.recordId}.${rule.dependentField}`, isRequired);
        }
      }
    });

    record.updateFieldRulesData(fieldsRulesData);
  }
}
